import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Trash2, FileText, Image, Receipt, ExternalLink } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Modal } from '@/components/ui/Modal';
import { Pagination } from '@/components/ui/Pagination';
import { FileUpload } from '@/components/ui/FileUpload';
import { createAnexoInput } from '@portal-leo/shared';
import type { z } from 'zod';

type CreateAnexoInput = z.infer<typeof createAnexoInput>;

interface TabAnexosProps {
  eventoId: string;
}

const categorias = [
  { value: 'RECIBO', label: 'Recibo', icon: Receipt, className: 'bg-green-100 text-green-600' },
  { value: 'IMAGEM', label: 'Imagem', icon: Image, className: 'bg-blue-100 text-blue-600' },
  { value: 'DOCUMENTO', label: 'Documento', icon: FileText, className: 'bg-gray-100 text-gray-600' },
];

export function TabAnexos({ eventoId }: TabAnexosProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const utils = trpc.useUtils();

  const { data, isLoading } = trpc.eventos.listAnexos.useQuery({ id: eventoId, page, limit: 20 });
  const anexos = data?.items;

  const addMutation = trpc.eventos.addAnexo.useMutation({
    onSuccess: () => {
      utils.eventos.listAnexos.invalidate();
      utils.eventos.getById.invalidate({ id: eventoId });
      handleCloseModal();
    },
  });

  const removeMutation = trpc.eventos.removeAnexo.useMutation({
    onSuccess: () => {
      utils.eventos.listAnexos.invalidate();
      utils.eventos.getById.invalidate({ id: eventoId });
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CreateAnexoInput>({
    resolver: zodResolver(createAnexoInput),
    defaultValues: {
      eventoId,
      categoria: 'DOCUMENTO',
    },
  });

  const urlArquivoValue = watch('urlArquivo');

  const onSubmit = (data: CreateAnexoInput) => {
    addMutation.mutate(data);
  };

  const handleOpenModal = () => {
    reset({
      eventoId,
      nomeArquivo: '',
      urlArquivo: '',
      tipoArquivo: '',
      categoria: 'DOCUMENTO',
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    reset();
  };

  const handleRemove = (id: string) => {
    if (window.confirm('Tem certeza que deseja remover este anexo?')) {
      removeMutation.mutate({ id });
    }
  };

  const getCategoriaInfo = (categoria: string) => {
    return categorias.find((c) => c.value === categoria) ?? categorias[2]!;
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">Total de anexos</p>
          <p className="text-lg font-bold">{anexos?.length || 0}</p>
        </div>
        <Button onClick={handleOpenModal}>
          <Plus className="mr-2 h-4 w-4" />
          Novo Anexo
        </Button>
      </div>

      {anexos && anexos.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {anexos.map((anexo) => {
            const catInfo = getCategoriaInfo(anexo.categoria);
            const CatIcon = catInfo.icon;
            const isImage = anexo.categoria === 'IMAGEM' || anexo.tipoArquivo?.startsWith('image/');

            return (
              <Card key={anexo.id}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${catInfo.className}`}>
                      <CatIcon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate" title={anexo.nomeArquivo}>
                        {anexo.nomeArquivo}
                      </p>
                      <p className="text-sm text-gray-500">{catInfo.label}</p>
                      <p className="text-xs text-gray-400">{formatDate(anexo.createdAt)}</p>
                    </div>
                  </div>

                  {isImage && (
                    <div className="mt-3">
                      <img
                        src={anexo.urlArquivo}
                        alt={anexo.nomeArquivo}
                        className="w-full h-32 object-cover rounded-lg"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </div>
                  )}

                  <div className="mt-3 flex items-center justify-between">
                    <a
                      href={anexo.urlArquivo}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Abrir
                    </a>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemove(anexo.id)}
                      disabled={removeMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="py-8 text-center text-gray-500">
            <p>Nenhum anexo cadastrado</p>
          </CardContent>
        </Card>
      )}

      {data && <Pagination meta={data.meta} onPageChange={setPage} itemLabel="anexos" />}

      <Modal isOpen={isModalOpen} onClose={handleCloseModal} title="Novo Anexo">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <input type="hidden" {...register('eventoId')} />

          <FileUpload
            label="Arquivo"
            required
            accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
            value={urlArquivoValue || undefined}
            onUploadComplete={(url, meta) => {
              setValue('urlArquivo', url, { shouldValidate: true });
              if (meta?.nome) setValue('nomeArquivo', meta.nome);
              if (meta?.tipo) {
                setValue('tipoArquivo', meta.tipo);
                if (meta.tipo.startsWith('image/')) {
                  setValue('categoria', 'IMAGEM');
                } else if (meta.tipo === 'application/pdf') {
                  setValue('categoria', 'RECIBO');
                }
              }
            }}
            onClear={() => {
              setValue('urlArquivo', '' as never);
              setValue('nomeArquivo', '');
              setValue('tipoArquivo', '');
              setValue('categoria', 'DOCUMENTO');
            }}
            error={errors.urlArquivo?.message}
          />

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Categoria</label>
              <select
                {...register('categoria')}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                {categorias.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={handleCloseModal}>
              Cancelar
            </Button>
            <Button type="submit" isLoading={addMutation.isPending}>
              Adicionar
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
